import { useMemo, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { lightTheme, darkTheme } from '@/theme/theme';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { LocaleProvider } from '@/lib/i18n';
import { AnalyticsProvider } from '@/lib/analytics';
import { AppShell } from '@/components/AppShell';
import { JudgeShell } from '@/components/JudgeShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { useDomain } from '@/hooks/useDomain';

// Lightweight pages - loaded eagerly
import { DashboardPage } from '@/features/admin/DashboardPage';
import { TeamPage } from '@/features/admin/TeamPage';
import { GamePage } from '@/features/admin/GamePage';
import { BlockPage } from '@/features/admin/BlockPage';
import { CourtPage } from '@/features/admin/CourtPage';
import { TimetablePage } from '@/features/admin/TimetablePage';
import { JudgePage } from '@/features/judge/JudgePage';
import { CreateUserPage } from '@/features/judge/CreateUserPage';
import { RefereeDetailPage } from '@/features/admin/RefereeDetailPage';
import { MyInformationPage } from '@/features/profile/MyInformationPage';
import { TeamPrintPage, JudgePrintPage } from '@/features/print';
import { OverviewPage } from '@/features/admin/OverviewPage';
import { SettingsPage } from '@/features/admin/SettingsPage';
import { AnalyticsPage } from '@/features/admin/AnalyticsPage';
import { PrintingPage } from '@/features/admin/PrintingPage';
import { HeadJudgeLogPage } from '@/features/admin/HeadJudgeLogPage';
import { FaqPage } from '@/features/admin/FaqPage';

// Heavy pages with DataGrid - lazy loaded
const GamesPage = lazy(() => import('@/features/admin/GamesPage').then(m => ({ default: m.GamesPage })));
const TeamsPage = lazy(() => import('@/features/admin/TeamsPage').then(m => ({ default: m.TeamsPage })));
const TeamDiagnosticsPage = lazy(() => import('@/features/admin/TeamDiagnosticsPage').then(m => ({ default: m.TeamDiagnosticsPage })));
const UsersPage = lazy(() => import('@/features/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const BlocksPage = lazy(() => import('@/features/admin/BlocksPage').then(m => ({ default: m.BlocksPage })));
const CourtsPage = lazy(() => import('@/features/admin/CourtsPage').then(m => ({ default: m.CourtsPage })));
const FieldsPage = lazy(() => import('@/features/admin/FieldsPage').then(m => ({ default: m.FieldsPage })));
const FieldGroupsPage = lazy(() => import('@/features/admin/FieldGroupsPage').then(m => ({ default: m.FieldGroupsPage })));
const CourtPrefsPage = lazy(() => import('@/features/admin/CourtPrefsPage').then(m => ({ default: m.CourtPrefsPage })));
const RefereePage = lazy(() => import('@/features/referee/RefereePage').then(m => ({ default: m.RefereePage })));

// Map pages with Leaflet - lazy loaded
const FieldMapPage = lazy(() => import('@/features/admin/FieldMapPage').then(m => ({ default: m.FieldMapPage })));

// Spectator pages - lazy loaded (public, no auth required)
const LiveShell = lazy(() => import('@/features/live').then(m => ({ default: m.LiveShell })));
const SpectatorHomePage = lazy(() => import('@/features/live').then(m => ({ default: m.HomePage })));
const SpectatorLeagueTabsPage = lazy(() => import('@/features/live/pages/LeagueTabsPage').then(m => ({ default: m.LeagueTabsPage })));
const SpectatorTeamPage = lazy(() => import('@/features/live').then(m => ({ default: m.TeamPage })));
const SpectatorBlockPage = lazy(() => import('@/features/live').then(m => ({ default: m.BlockPage })));
const SpectatorFieldPage = lazy(() => import('@/features/live').then(m => ({ default: m.FieldPage })));
const SpectatorMyTeamsPage = lazy(() => import('@/features/live').then(m => ({ default: m.MyTeamsPage })));
const SpectatorBigscreenPage = lazy(() => import('@/features/live').then(m => ({ default: m.BigscreenPage })));
const SpectatorMapPage = lazy(() => import('@/features/live').then(m => ({ default: m.MapPage })));
const SpectatorRestaurantsPage = lazy(() => import('@/features/live').then(m => ({ default: m.RestaurantsPage })));

// App Admin pages (staff only, with dnd-kit) - lazy loaded
const AppAdminShell = lazy(() => import('@/features/app-admin').then(m => ({ default: m.AppAdminShell })));
const AppAdminDashboardPage = lazy(() => import('@/features/app-admin').then(m => ({ default: m.DashboardPage })));
const MapAdminPage = lazy(() => import('@/features/app-admin').then(m => ({ default: m.MapAdminPage })));
const RestaurantsAdminPage = lazy(() => import('@/features/app-admin').then(m => ({ default: m.RestaurantsAdminPage })));
const TranslationsAdminPage = lazy(() => import('@/features/app-admin').then(m => ({ default: m.TranslationsAdminPage })));
const AnalyticsAdminPage = lazy(() => import('@/features/app-admin').then(m => ({ default: m.AnalyticsAdminPage })));

// Loading fallback component
function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Box>
  );
}

// Create QueryClient for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

/**
 * Smart redirect based on user role (for tuho domain).
 * Admins go to /admin, regular users go to /judge.
 */
function StaffRootRedirect() {
  const { user } = useAuthStore();
  return <Navigate to={user?.is_staff ? '/admin' : '/judge'} replace />;
}

/**
 * Spectator App Routes (app.kyykka.fi)
 * Public routes served at root - no authentication required.
 * Admin routes at /admin/* require staff authentication.
 */
function SpectatorRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public login for app admin */}
        <Route path="/login" element={<LoginPage />} />

        {/* App Admin routes (staff only) */}
        <Route
          element={
            <ProtectedRoute>
              <AppAdminShell />
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<AppAdminDashboardPage />} />
          <Route path="/admin/restaurants" element={<RestaurantsAdminPage />} />
          <Route path="/admin/map" element={<MapAdminPage />} />
          <Route path="/admin/translations" element={<TranslationsAdminPage />} />
          <Route path="/admin/analytics" element={<AnalyticsAdminPage />} />
        </Route>

        {/* Spectator routes at root */}
        <Route element={<LiveShell />}>
          <Route path="/" element={<SpectatorHomePage />} />
          <Route path="/league/:league" element={<SpectatorLeagueTabsPage />} />
          <Route path="/team/:teamId" element={<SpectatorTeamPage />} />
          <Route path="/block/:blockId" element={<SpectatorBlockPage />} />
          <Route path="/field/:fieldId" element={<SpectatorFieldPage />} />
          <Route path="/my-teams" element={<SpectatorMyTeamsPage />} />
          <Route path="/map" element={<SpectatorMapPage />} />
          <Route path="/restaurants" element={<SpectatorRestaurantsPage />} />
        </Route>

        {/* Bigscreen - standalone, no shell */}
        <Route path="/bigscreen" element={<SpectatorBigscreenPage />} />
        <Route path="/bigscreen/:league" element={<SpectatorBigscreenPage />} />

        {/* 404 - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Staff Routes (tuho.kyykka.fi)
 * Judge interface at root, admin at /admin/*, authentication required.
 */
function StaffRoutes() {
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff ?? false;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<CreateUserPage />} />

        {/* Admin routes with AppShell (staff only) */}
        {isAdmin && (
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<DashboardPage />} />
            <Route path="/admin/head-judge-log" element={<HeadJudgeLogPage />} />
            <Route path="/admin/overview" element={<OverviewPage />} />
            <Route path="/admin/field-map" element={<FieldMapPage />} />
            <Route path="/admin/games" element={<GamesPage />} />
            <Route path="/admin/games/:gameId" element={<GamePage />} />
            <Route path="/admin/teams" element={<TeamsPage />} />
            <Route path="/admin/teams/diagnostics" element={<TeamDiagnosticsPage />} />
            <Route path="/admin/teams/:teamId" element={<TeamPage />} />
            <Route path="/admin/blocks" element={<BlocksPage />} />
            <Route path="/admin/blocks/:blockId" element={<BlockPage />} />
            <Route path="/admin/courts" element={<CourtsPage />} />
            <Route path="/admin/courts/:courtId" element={<CourtPage />} />
            <Route path="/admin/fields" element={<FieldsPage />} />
            <Route path="/admin/field-groups" element={<FieldGroupsPage />} />
            <Route path="/admin/timetable" element={<TimetablePage />} />
            <Route path="/admin/court-prefs" element={<CourtPrefsPage />} />
            <Route path="/admin/referees" element={<RefereePage />} />
            <Route path="/admin/referees/:refereeId" element={<RefereeDetailPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/printing" element={<PrintingPage />} />
            <Route path="/admin/analytics" element={<AnalyticsPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            <Route path="/admin/faq" element={<FaqPage />} />
            {/* Admins can also access judge via AppShell */}
            <Route path="/judge/*" element={<JudgePage />} />
            <Route path="/profile" element={<MyInformationPage />} />
          </Route>
        )}

        {/* Non-admin routes with JudgeShell (minimal UI) */}
        {!isAdmin && (
          <Route
            element={
              <ProtectedRoute>
                <JudgeShell />
              </ProtectedRoute>
            }
          >
            <Route path="/judge/*" element={<JudgePage />} />
            <Route path="/profile" element={<MyInformationPage />} />
          </Route>
        )}

        {/* Print routes (standalone, no shell) */}
        <Route
          path="/print/teams"
          element={
            <ProtectedRoute>
              <TeamPrintPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/print/judges"
          element={
            <ProtectedRoute>
              <JudgePrintPage />
            </ProtectedRoute>
          }
        />

        {/* Root redirect based on role */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <StaffRootRedirect />
            </ProtectedRoute>
          }
        />

        {/* 404 - redirect to appropriate home */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <StaffRootRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function AppContent() {
  const { themeMode } = useUIStore();
  const domain = useDomain();
  const theme = useMemo(
    () => (themeMode === 'dark' ? darkTheme : lightTheme),
    [themeMode]
  );

  // Only enable analytics on the spectator app (app.kyykka.fi)
  const analyticsEnabled = domain === 'app';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AnalyticsProvider disabled={!analyticsEnabled}>
          {domain === 'app' ? <SpectatorRoutes /> : <StaffRoutes />}
        </AnalyticsProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <AppContent />
      </LocaleProvider>
    </QueryClientProvider>
  );
}
